// frontend/src/components/churches/DeleteChurchModal.tsx

import React, { useState } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { ChurchWithStats, ChurchUsageStats } from '../../services/churchService';
import { AlertTriangle } from 'lucide-react';

interface DeleteChurchModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: (force: boolean) => void;
  church: ChurchWithStats | null;
  usageStats: ChurchUsageStats | null;
}

export const DeleteChurchModal: React.FC<DeleteChurchModalProps> = ({
  show,
  onHide,
  onConfirm,
  church,
  usageStats
}) => {
  const [forceDelete, setForceDelete] = useState(false);

  if (!church || !usageStats) return null;

  const canDeleteSafely = usageStats.can_delete_safely;
  const hasData = usageStats.total_count > 0;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <AlertTriangle className="text-warning me-2" size={24} />
          Delete Church
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Are you sure you want to delete <strong>{church.name}</strong>?
        </p>

        {hasData && (
          <Alert variant="warning">
            <Alert.Heading>This church has existing data:</Alert.Heading>
            <ul className="mb-0">
              {usageStats.service_count > 0 && (
                <li><strong>{usageStats.service_count}</strong> service(s)</li>
              )}
              {usageStats.template_count > 0 && (
                <li><strong>{usageStats.template_count}</strong> service music template(s)</li>
              )}
              {usageStats.user_count > 0 && (
                <li><strong>{usageStats.user_count}</strong> user(s) with this as primary church</li>
              )}
              {usageStats.user_churches_count > 0 && (
                <li><strong>{usageStats.user_churches_count}</strong> user-church association(s)</li>
              )}
            </ul>
          </Alert>
        )}

        {hasData && (
          <>
            <Alert variant="danger">
              <strong>Warning:</strong> Deleting this church will CASCADE delete:
              <ul className="mb-0 mt-2">
                <li>All services for this church</li>
                <li>All service music templates for this church</li>
                <li>All service definitions for this church</li>
                <li>All user-church associations</li>
                <li>All church settings</li>
              </ul>
            </Alert>

            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="forceDeleteCheckbox"
                checked={forceDelete}
                onChange={(e) => setForceDelete(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="forceDeleteCheckbox">
                I understand that this will permanently delete all related data.
              </label>
            </div>
          </>
        )}

        {canDeleteSafely && (
          <Alert variant="info">
            This church has no associated data and can be safely deleted.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={() => onConfirm(hasData && forceDelete)}
          disabled={hasData && !forceDelete}
        >
          {hasData ? 'Delete with All Data' : 'Delete Church'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
